/**
 * Web Push subscription management. Registers the service worker and lets a
 * signed-in user subscribe or unsubscribe this device. Subscribing is only
 * ever triggered by an explicit click, never on page load.
 */

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

function isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function isIos() {
    return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
}

function urlBase64ToUint8Array(base64) {
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const raw = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));

    return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

async function send(method, url, body) {
    const response = await fetch(url, {
        method,
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-CSRF-TOKEN': csrfToken(),
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        throw new Error(`Push request failed (${response.status})`);
    }
}

async function registration() {
    return navigator.serviceWorker.register('/sw.js');
}

async function currentSubscription() {
    if (!isSupported()) {
        return null;
    }

    const reg = await navigator.serviceWorker.getRegistration('/');

    return reg ? reg.pushManager.getSubscription() : null;
}

window.pushNotifications = {
    isSupported,
    // iOS only allows web push from an installed (home screen) web app.
    needsInstall: () => isIos() && !isStandalone(),
    isDenied: () => isSupported() && Notification.permission === 'denied',
    async isSubscribed() {
        return (await currentSubscription()) !== null;
    },
    /** Resolves to 'enabled', 'denied', 'unsupported' or 'unconfigured'. */
    async enable(urls) {
        const publicKey = document.querySelector('meta[name="vapid-public-key"]')?.content;

        if (!isSupported()) {
            return 'unsupported';
        }

        if (!publicKey) {
            return 'unconfigured';
        }

        if ((await Notification.requestPermission()) !== 'granted') {
            return 'denied';
        }

        const reg = await registration();
        await navigator.serviceWorker.ready;

        const subscription =
            (await reg.pushManager.getSubscription()) ??
            (await reg.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(publicKey),
            }));

        await send('POST', urls.store, {
            ...subscription.toJSON(),
            contentEncoding: (PushManager.supportedContentEncodings || ['aesgcm'])[0],
        });

        // Push covers being notified while the site is closed; also turn on
        // the lighter foreground notifications so the two don't drift apart.
        window.messageNotifications?.enable();

        return 'enabled';
    },
    async disable(urls) {
        const subscription = await currentSubscription();

        window.messageNotifications?.disable();

        if (!subscription) {
            return;
        }

        await send('DELETE', urls.destroy, { endpoint: subscription.endpoint });
        await subscription.unsubscribe();
    },
};

// Registering on load makes the site installable and keeps the worker current.
if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => registration().catch(() => {}));
}
